import type { PointerPayload } from "../envelope";

/**
 * pointerModel - the live pointer's pure client-side model (D-06).
 *
 * The pointer is visibly temporary and is never persisted anywhere: no API call, no store
 * beyond an in-memory map keyed by sender identity, no history. The throttle below is a UX and
 * bandwidth guard only, not a security boundary - a malicious client can send faster than this;
 * the receiving side is protected by decodeEnvelope's per-topic size check and by the fact that
 * this store only ever keeps one entry per sender (T-05-21).
 */

// About 15 messages per second (D-01).
export const POINTER_MIN_INTERVAL_MS = 67;
// D-06: "fades after about 2s".
export const POINTER_FADE_AFTER_MS = 2000;
export const POINTER_FADE_DURATION_MS = 500;

export type PointerThrottle = {
  shouldSend(nowMs: number): boolean;
  forceNext(): void;
};

export function createPointerThrottle(): PointerThrottle {
  let lastSentMs: number | null = null;
  let forced = false;

  return {
    shouldSend(nowMs: number): boolean {
      if (forced || lastSentMs === null || nowMs - lastSentMs >= POINTER_MIN_INTERVAL_MS) {
        forced = false;
        lastSentMs = nowMs;
        return true;
      }
      return false;
    },
    forceNext(): void {
      forced = true;
    },
  };
}

export function pointerOpacity(lastSeenMs: number, nowMs: number): number {
  const elapsed = nowMs - lastSeenMs;
  if (elapsed <= POINTER_FADE_AFTER_MS) {
    return 1;
  }
  if (elapsed >= POINTER_FADE_AFTER_MS + POINTER_FADE_DURATION_MS) {
    return 0;
  }
  return 1 - (elapsed - POINTER_FADE_AFTER_MS) / POINTER_FADE_DURATION_MS;
}

export type RemotePointer = {
  identity: string;
  payload: PointerPayload;
  lastSeenMs: number;
};

export type PointerStore = ReadonlyMap<string, RemotePointer>;

export function receivePointer(
  store: PointerStore,
  identity: string,
  payload: PointerPayload,
  nowMs: number,
): PointerStore {
  const next = new Map(store);
  next.set(identity, { identity, payload, lastSeenMs: nowMs });
  return next;
}

export function hidePointer(store: PointerStore, identity: string): PointerStore {
  if (!store.has(identity)) {
    return store;
  }
  const next = new Map(store);
  next.delete(identity);
  return next;
}

export function prunePointers(store: PointerStore, nowMs: number): PointerStore {
  const next = new Map(store);
  let changed = false;
  for (const [identity, pointer] of store) {
    if (pointerOpacity(pointer.lastSeenMs, nowMs) <= 0) {
      next.delete(identity);
      changed = true;
    }
  }
  return changed ? next : store;
}

export function visiblePointers(
  store: PointerStore,
  nowMs: number,
  source: { kind: "track" | "frame"; id: string },
): Array<RemotePointer & { opacity: number }> {
  const result: Array<RemotePointer & { opacity: number }> = [];
  for (const pointer of store.values()) {
    if (pointer.payload.src.kind !== source.kind || pointer.payload.src.id !== source.id) {
      continue;
    }
    const opacity = pointerOpacity(pointer.lastSeenMs, nowMs);
    if (opacity > 0) {
      result.push({ ...pointer, opacity });
    }
  }
  return result;
}
